/**
 * Signatory Matrix Variant Path Helpers
 *
 * The Signatory Matrix supports the same STC/Military variant split as Database,
 * reusing the same variant descriptors (STC_VARIANT / MILITARY_VARIANT) from
 * Database/_variants. Instance data (Signatories, Testers, Revisions, OpenForms
 * applications, counts) is parallel per variant, while catalog data (Panels,
 * ApprovedFunctions, InterviewQuestions, Types, TesterTypes) is shared on the STC
 * tree with STCApproved / MilitaryApproved flags.
 *
 * This file provides the single source of truth for matrix Firebase paths so later
 * PRs (#12–#16) can migrate routes, actions, UI, OpenForms, and RTDB rules without
 * introducing errors or breaking existing STC data.
 * 
 * See UID_KEY.md for the person-key contract and uid-key migration plan (#113).
 */

import {
    isMilitaryEnabled,
} from "../../Database/_variants/index.js";

/**
 * Matrix-specific variant descriptors.
 * 
 * These mirror the Database variants but use Matrix-specific app names,
 * icons, and positions to separate the Signatory Matrix from the Database
 * in the app switcher and navigation.
 * 
 * @typedef {Object} Variant
 * @property {"stc"|"military"} key             - canonical variant identifier
 * @property {string}           label           - human-readable applet name
 * @property {string}           app             - meta.app value (single token, no spaces)
 * @property {string}           appIcon         - icon for the app switcher
 * @property {number}           appSwitcherPosition
 * @property {string}           collectionPrefix - "" or "Military" — prepended to entity collection names
 * @property {string}           security         - meta.security base path
 */

/**
 * STC Signatory Matrix variant — the historical default. Uses the un-prefixed
 * Firebase collections under Matrices/SignatoryMatrix.
 */
export const STC_VARIANT = Object.freeze({
    key: "stc",
    label: "Competence & Matrices",
    app: "Matrices",
    appIcon: "book",
    appSwitcherPosition: 3,
    collectionPrefix: "",
    security: "Matrices.Read",
});

/**
 * Military Signatory Matrix variant — parallel Firebase collections under
 * Matrices/MilitarySignatoryMatrix.
 */
export const MILITARY_VARIANT = Object.freeze({
    key: "military",
    label: "Military Signatory Matrix",
    app: "MilitaryMatrices",
    appIcon: "book-skull",
    appSwitcherPosition: 10,
    collectionPrefix: "Military",
    security: "Military.Read",
});

// Re-export isMilitaryEnabled from Database variants
export { isMilitaryEnabled };

/**
 * All registered Signatory Matrix variants for the current build.
 * @returns {Variant[]}
 */
export const allVariants = () => isMilitaryEnabled()
    ? [STC_VARIANT, MILITARY_VARIANT]
    : [STC_VARIANT];

// ---------------------------------------------------------------------------
// Matrix Instance Data Paths (parallel per variant)
// ---------------------------------------------------------------------------

/**
 * Base root path for a matrix variant's instance data.
 * STC: `Matrices/SignatoryMatrix`
 * Military: `Matrices/MilitarySignatoryMatrix`
 * @param {Variant} variant
 * @returns {string}
 */
export const matrixRoot = (variant) =>
    `Matrices/${variant.collectionPrefix}SignatoryMatrix`;

/**
 * Signatories collection path (the main matrix roster).
 * STC: `Matrices/SignatoryMatrix/Signatories`
 * Military: `Matrices/MilitarySignatoryMatrix/Signatories`
 * @param {Variant} variant
 * @returns {string}
 */
export const signatoriesPath = (variant) =>
    `${matrixRoot(variant)}/Signatories`;

/**
 * Single signatory record path.
 * @param {Variant} variant
 * @param {string} signatoryId
 * @returns {string}
 */
export const signatoryPath = (variant, signatoryId) =>
    `${signatoriesPath(variant)}/${signatoryId}`;

/**
 * Testers collection path (test witnesses).
 * STC: `Matrices/SignatoryMatrix/Testers`
 * Military: `Matrices/MilitarySignatoryMatrix/Testers`
 * @param {Variant} variant
 * @returns {string}
 */
export const testersPath = (variant) =>
    `${matrixRoot(variant)}/Testers`;

/**
 * Single tester record path.
 * @param {Variant} variant
 * @param {string} testerId
 * @returns {string}
 */
export const testerPath = (variant, testerId) =>
    `${testersPath(variant)}/${testerId}`;

/**
 * Revisions root path (snapshot history for approval workflow).
 * STC: `Matrices/SignatoryMatrix/Revisions`
 * Military: `Matrices/MilitarySignatoryMatrix/Revisions`
 * @param {Variant} variant
 * @returns {string}
 */
export const revisionsPath = (variant) =>
    `${matrixRoot(variant)}/Revisions`;

/**
 * Current revision number pointer (e.g. "A", "B", "C").
 * STC: `Matrices/SignatoryMatrix/Revisions/Revision`
 * Military: `Matrices/MilitarySignatoryMatrix/Revisions/Revision`
 * @param {Variant} variant
 * @returns {string}
 */
export const revisionNumberPath = (variant) =>
    `${revisionsPath(variant)}/Revision`;

/**
 * Current revision ID pointer (push key, e.g. "--R...").
 * STC: `Matrices/SignatoryMatrix/Revisions/RevisionID`
 * Military: `Matrices/MilitarySignatoryMatrix/Revisions/RevisionID`
 * @param {Variant} variant
 * @returns {string}
 */
export const revisionIdPath = (variant) =>
    `${revisionsPath(variant)}/RevisionID`;

/**
 * Revisions snapshots collection.
 * @param {Variant} variant
 * @returns {string}
 */
export const revisionSnapshotsPath = (variant) =>
    `${revisionsPath(variant)}/Revisions`;

/**
 * Single revision snapshot path.
 * @param {Variant} variant
 * @param {string} revisionId
 * @returns {string}
 */
export const revisionSnapshotPath = (variant, revisionId) =>
    `${revisionSnapshotsPath(variant)}/${revisionId}`;

/**
 * Revisions refs (likely used for indexing or quick lookups).
 * @param {Variant} variant
 * @returns {string}
 */
export const revisionRefsPath = (variant) =>
    `${revisionsPath(variant)}/Refs`;

/**
 * OpenForms signatory applications inbox path (new applications pending approval).
 * STC: `OpenForms/Signatories`
 * Military: `OpenForms/MilitarySignatories`
 * @param {Variant} variant
 * @returns {string}
 */
export const openFormsSignatories = (variant) =>
    variant.collectionPrefix
        ? `OpenForms/${variant.collectionPrefix}Signatories`
        : `OpenForms/Signatories`;

/**
 * Single OpenForms application path.
 * @param {Variant} variant
 * @param {string} applicationId
 * @returns {string}
 */
export const openFormsSignatory = (variant, applicationId) =>
    `${openFormsSignatories(variant)}/${applicationId}`;

// ---------------------------------------------------------------------------
// Global Counters (TSR / CSR numbers)
// ---------------------------------------------------------------------------

/**
 * Global counter path for CSR/TSR numbers (signatory roster numbers).
 * STC: `counts/CSR/amount`
 * Military: `counts/military_CSR/amount`
 *
 * Follows the same pattern as Database entity counters — see
 * Database/_variants/index.js `globalCounterPath()`.
 *
 * @param {Variant} variant
 * @returns {string}
 */
export const csrCountPath = (variant) =>
    variant.key === "stc"
        ? "counts/CSR/amount"
        : "counts/military_CSR/amount";

/**
 * Read the current CSR counter value from the Vuex store.
 * STC: `store.state.counts.CSR.amount`
 * Military: `store.state.counts.military_CSR.amount`
 *
 * @param {object} store
 * @param {Variant} variant
 * @returns {number}
 */
export const readCsrCount = (store, variant) => {
    const node = variant.key === "stc"
        ? store?.state?.counts?.CSR
        : store?.state?.counts?.military_CSR;
    return parseInt(node?.amount || 0);
};

// ---------------------------------------------------------------------------
// Catalog Data Paths (shared on STC tree, gated by STCApproved / MilitaryApproved)
// ---------------------------------------------------------------------------
//
// The catalog collections below live ONLY on the STC tree. Records are gated
// with `STCApproved` / `MilitaryApproved` flags so the UI can filter by variant.
// There is no parallel Military tree for these collections.
//
// These helpers return the single canonical path and do not vary by variant.
// A future PR will migrate the UI to use these helpers and filter by the flag.

/**
 * Panels catalog (disciplines/areas of expertise).
 * Canonical path: `Matrices/SignatoryMatrix/Panels`
 * @returns {string}
 */
export const panelsPath = () => "Matrices/SignatoryMatrix/Panels";

/**
 * Single panel path.
 * @param {string} panelId
 * @returns {string}
 */
export const panelPath = (panelId) => `${panelsPath()}/${panelId}`;

/**
 * Approved Functions catalog (roles/authorities).
 * Canonical path: `Matrices/SignatoryMatrix/ApprovedFunctions`
 * @returns {string}
 */
export const approvedFunctionsPath = () => "Matrices/SignatoryMatrix/ApprovedFunctions";

/**
 * Single approved function path.
 * @param {string} functionId
 * @returns {string}
 */
export const approvedFunctionPath = (functionId) =>
    `${approvedFunctionsPath()}/${functionId}`;

/**
 * Interview Questions catalog (competency assessment questions).
 * Canonical path: `Matrices/SignatoryMatrix/InterviewQuestions`
 * @returns {string}
 */
export const interviewQuestionsPath = () => "Matrices/SignatoryMatrix/InterviewQuestions";

/**
 * Single interview question path.
 * @param {string} questionId
 * @returns {string}
 */
export const interviewQuestionPath = (questionId) =>
    `${interviewQuestionsPath()}/${questionId}`;

/**
 * Types catalog (signatory types / categories).
 * Canonical path: `Matrices/SignatoryMatrix/Types`
 * @returns {string}
 */
export const typesPath = () => "Matrices/SignatoryMatrix/Types";

/**
 * Single type path.
 * @param {string} typeId
 * @returns {string}
 */
export const typePath = (typeId) => `${typesPath()}/${typeId}`;

/**
 * Tester Types catalog (test witness categories).
 * Canonical path: `Matrices/SignatoryMatrix/TesterTypes`
 * @returns {string}
 */
export const testerTypesPath = () => "Matrices/SignatoryMatrix/TesterTypes";

/**
 * Single tester type path.
 * @param {string} testerTypeId
 * @returns {string}
 */
export const testerTypePath = (testerTypeId) =>
    `${testerTypesPath()}/${testerTypeId}`;

// ---------------------------------------------------------------------------
// URL Route Helpers (parallel per variant)
// ---------------------------------------------------------------------------

/**
 * URL path for a matrix variant's main list view.
 * STC: `/Matrices/SignatoryMatrix/Signatories`
 * Military: `/Matrices/MilitarySignatoryMatrix/Signatories`
 *
 * The URL structure mirrors the Firebase path structure for consistency.
 *
 * @param {Variant} variant
 * @returns {string}
 */
export const matrixRouteRoot = (variant) =>
    `/Matrices/${variant.collectionPrefix}SignatoryMatrix`;

/**
 * Signatories list route.
 * @param {Variant} variant
 * @returns {string}
 */
export const signatoriesRoute = (variant) =>
    `${matrixRouteRoot(variant)}/Signatories`;

/**
 * Single signatory detail route.
 * @param {Variant} variant
 * @param {string} signatoryId
 * @returns {string}
 */
export const signatoryRoute = (variant, signatoryId) =>
    `${signatoriesRoute(variant)}/${signatoryId}`;

/**
 * Testers list route.
 * @param {Variant} variant
 * @returns {string}
 */
export const testersRoute = (variant) =>
    `${matrixRouteRoot(variant)}/Testers`;

/**
 * Single tester detail route.
 * @param {Variant} variant
 * @param {string} testerId
 * @returns {string}
 */
export const testerRoute = (variant, testerId) =>
    `${testersRoute(variant)}/${testerId}`;

/**
 * Settings route (catalog editing — shared, no variant needed).
 * @returns {string}
 */
export const settingsRoute = () => "/Matrices/SignatoryMatrix/Settings";

/**
 * Form4 (Post) Holders list route.
 * @param {Variant} variant
 * @returns {string}
 */
export const form4HoldersRoute = (variant) =>
    `${matrixRouteRoot(variant)}/Form4Holders`;

/**
 * New signatory applications list route.
 * @param {Variant} variant
 * @returns {string}
 */
export const newSignatoriesRoute = (variant) =>
    `${matrixRouteRoot(variant)}/NewSignatories`;

/**
 * Single new signatory application detail route.
 * @param {Variant} variant
 * @param {string} applicationId
 * @returns {string}
 */
export const newSignatoryRoute = (variant, applicationId) =>
    `${newSignatoriesRoute(variant)}/${applicationId}`;

/**
 * Self-view route (current user's own signatory record).
 * @param {Variant} variant
 * @returns {string}
 */
export const selfRoute = (variant) =>
    `${matrixRouteRoot(variant)}/Self`;

/**
 * Unapproved changes view route (next revision preview).
 * @param {Variant} variant
 * @returns {string}
 */
export const unapprovedChangesRoute = (variant) =>
    `${matrixRouteRoot(variant)}/UnapprovedSignatoriesChanges`;
